import { useEffect, useState } from 'react';
import {
    BodyLargeEmphasized,
    BodyLargeRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    Input,
    LabelRegular,
    RadioInputRow,
    Spinner,
    useToastController,
    XStack,
    YStack,
} from 'smoothie';

import {
    useDeleteAllSyncteraExternalAccountsForUser,
    useDeleteSyncteraExternalAccount,
} from '../../../../banking/banking/hooks/useDeleteSyncteraExternalAccount';
import { useGetSyncteraExternalAccountsForUser } from '../../../../banking/banking/hooks/useGetSyncteraExternalAccountsForUser';
import { ConfirmModal } from '../../../../common/components/ConfirmModal';
import { GroveSimpleModal } from '../../../../common/components/GroveSimpleModal';
import {
    MutationErrorToastOptions,
    MutationSuccessToastOptions,
} from '../../../../common/components/Toasts';
import type { MemberDetails } from '../../../entityTypes';

// Membership cancellation accepts any string that contains CONFIRM.
const DELETE_ALL_CONFIRMATION = 'CONFIRM';

interface MemberExternalAccountsProps {
    user: MemberDetails;
}

export const MemberExternalAccounts = ({
    user,
}: MemberExternalAccountsProps) => {
    const [options, setOptions] = useState<
        Array<{
            syncteraExternalAccountId: string;
            accountMasked: string;
            nickname?: string;
            institutionName?: string | null;
            manualReviewStatus?: string | null;
        }>
    >([]);
    const [
        selectedSyncteraExternalAccountId,
        setSelectedSyncteraExternalAccountId,
    ] = useState<string | null>(null);
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [isDeleteAllModalOpen, setIsDeleteAllModalOpen] = useState(false);
    const [deleteAllConfirmText, setDeleteAllConfirmText] = useState('');

    const toast = useToastController();

    const {
        data: externalAccounts,
        isLoading: isLoadingExternalAccounts,
        refetch: refetchExternalAccounts,
        isRefetching,
    } = useGetSyncteraExternalAccountsForUser({ userId: user.id });

    const {
        mutateAsync: deleteSyncteraExternalAccount,
        isPending: isDeleting,
    } = useDeleteSyncteraExternalAccount();

    const {
        mutateAsync: deleteAllSyncteraExternalAccounts,
        isPending: isDeletingAll,
    } = useDeleteAllSyncteraExternalAccountsForUser();

    useEffect(() => {
        if (isLoadingExternalAccounts || externalAccounts == null) {
            setOptions([]);
            return;
        }

        const accountOptions = externalAccounts.map(
            ({
                syncteraExternalAccountId,
                accountMasked,
                nickname,
                institutionName,
                manualReviewStatus,
            }) => {
                return {
                    syncteraExternalAccountId,
                    accountMasked,
                    nickname,
                    institutionName,
                    manualReviewStatus,
                };
            },
        );

        setOptions(accountOptions);
        setSelectedSyncteraExternalAccountId(null);
    }, [JSON.stringify(externalAccounts), isLoadingExternalAccounts]);

    const onPressDelete = () => {
        setIsConfirmModalOpen(true);
    };

    const onCloseConfirmModal = () => {
        setIsConfirmModalOpen(false);
    };

    const onConfirmDelete = async () => {
        setIsConfirmModalOpen(false);

        if (selectedSyncteraExternalAccountId == null) return;

        try {
            await deleteSyncteraExternalAccount({
                urlParams: {
                    syncteraExternalAccountId:
                        selectedSyncteraExternalAccountId,
                },
            });

            void refetchExternalAccounts();
            setSelectedSyncteraExternalAccountId(null);

            toast.show('Success!', { duration: 2000 });
        } catch (e) {
            toast.show('Something went wrong!', { duration: 2000 });
        }
    };

    const onPressDeleteAll = () => {
        setDeleteAllConfirmText('');
        setIsDeleteAllModalOpen(true);
    };

    const onCloseDeleteAllModal = () => {
        if (isDeletingAll) return;

        setIsDeleteAllModalOpen(false);
        setDeleteAllConfirmText('');
    };

    const onConfirmDeleteAll = async () => {
        if (!deleteAllConfirmText.includes(DELETE_ALL_CONFIRMATION)) {
            return;
        }

        try {
            const {
                deletedSyncteraExternalAccountIds,
                failedSyncteraExternalAccountIds,
            } = await deleteAllSyncteraExternalAccounts({
                urlParams: { userId: user.id },
            });

            const deletedCount = deletedSyncteraExternalAccountIds.length;
            const failedCount = failedSyncteraExternalAccountIds.length;

            if (failedCount === 0) {
                toast.show(
                    'Deleted all external accounts',
                    MutationSuccessToastOptions,
                );
            } else {
                toast.show(
                    deletedCount === 0
                        ? 'Could not delete external accounts'
                        : `Deleted ${deletedCount} of ${deletedCount + failedCount} external accounts`,
                    MutationErrorToastOptions(
                        `${failedCount} could not be deleted.`,
                    ),
                );
            }
        } catch (e) {
            toast.show(
                'Could not delete external accounts',
                MutationErrorToastOptions(e),
            );
        } finally {
            setIsDeleteAllModalOpen(false);
            setDeleteAllConfirmText('');
            setSelectedSyncteraExternalAccountId(null);
            void refetchExternalAccounts();
        }
    };

    const isDeleteAllConfirmed = deleteAllConfirmText.includes(
        DELETE_ALL_CONFIRMATION,
    );
    const memberName = user.firstName ?? user.id;

    if (isLoadingExternalAccounts || isRefetching) {
        return (
            <YStack height={200} width={500} justifyContent="center">
                <Spinner />
            </YStack>
        );
    }

    return (
        <YStack gap={24} width={500} marginBottom={24}>
            <BodyLargeEmphasized textAlign="center">
                Delete Synctera external accounts for {memberName}
            </BodyLargeEmphasized>
            {options.length > 0 ? (
                <YStack>
                    {options.map(
                        ({
                            syncteraExternalAccountId,
                            nickname,
                            institutionName,
                            accountMasked,
                            manualReviewStatus,
                        }) => {
                            const vs =
                                manualReviewStatus != null
                                    ? `(${manualReviewStatus})`
                                    : '';
                            const inst =
                                institutionName != null
                                    ? `${institutionName} -`
                                    : '';
                            const label = `${inst} ${nickname} - *${accountMasked} ${vs}`;

                            return (
                                <RadioInputRow
                                    key={syncteraExternalAccountId}
                                    isSelected={
                                        selectedSyncteraExternalAccountId ===
                                        syncteraExternalAccountId
                                    }
                                    onPress={() => {
                                        setSelectedSyncteraExternalAccountId(
                                            syncteraExternalAccountId,
                                        );
                                    }}
                                    label={label}
                                    details={syncteraExternalAccountId}
                                />
                            );
                        },
                    )}
                    <YStack gap={12} marginTop={12}>
                        <Button
                            variant={ButtonVariant.PRIMARY}
                            size={ButtonSize.LARGE}
                            disabled={
                                selectedSyncteraExternalAccountId == null ||
                                isDeleting ||
                                isDeletingAll
                            }
                            tone="danger"
                            text="Delete"
                            onPress={onPressDelete}
                        />
                        <Button
                            variant={ButtonVariant.SECONDARY}
                            size={ButtonSize.LARGE}
                            disabled={isDeleting || isDeletingAll}
                            tone="danger"
                            text="Delete all"
                            onPress={onPressDeleteAll}
                        />
                    </YStack>
                </YStack>
            ) : (
                <BodyLargeRegular textAlign="center">
                    This member does not have any Synctera external accounts.
                </BodyLargeRegular>
            )}
            <ConfirmModal
                isOpen={isConfirmModalOpen}
                onClose={onCloseConfirmModal}
                onConfirm={() => {
                    void onConfirmDelete();
                }}
                description={`Delete ${selectedSyncteraExternalAccountId} - Are you sure?`}
            />
            <GroveSimpleModal
                isOpen={isDeleteAllModalOpen}
                onClose={onCloseDeleteAllModal}
            >
                <YStack gap={24} width={480} maxWidth="90vw">
                    <BodyLargeEmphasized textAlign="center">
                        {options.length === 1
                            ? `Delete the Synctera external account for ${memberName}?`
                            : `Delete all ${options.length} Synctera external accounts for ${memberName}?`}
                    </BodyLargeEmphasized>
                    <YStack gap={8}>
                        <LabelRegular>
                            Type &quot;CONFIRM&quot; to confirm
                        </LabelRegular>
                        <Input
                            size="tiny"
                            fontWeight="regular"
                            value={deleteAllConfirmText}
                            onChangeText={setDeleteAllConfirmText}
                            disabled={isDeletingAll}
                            autoCapitalize="characters"
                        />
                    </YStack>
                    <XStack
                        gap={24}
                        alignSelf="center"
                        flexWrap="wrap"
                        justifyContent="center"
                    >
                        <Button
                            variant={ButtonVariant.SECONDARY}
                            size={ButtonSize.LARGE}
                            text="Not quite"
                            disabled={isDeletingAll}
                            onPress={onCloseDeleteAllModal}
                        />
                        <Button
                            variant={ButtonVariant.PRIMARY}
                            size={ButtonSize.LARGE}
                            tone="danger"
                            text={isDeletingAll ? 'Deleting...' : 'Delete all'}
                            disabled={!isDeleteAllConfirmed || isDeletingAll}
                            onPress={() => {
                                void onConfirmDeleteAll();
                            }}
                        />
                    </XStack>
                </YStack>
            </GroveSimpleModal>
        </YStack>
    );
};
